import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Package2, ChevronDown } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { getStockItems, StockItem, createStockTransaction } from '../lib/stockItems';
import { ProductPhotoService } from '../services/productPhotoService';

interface ReceiveStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ReceiveStockModal({
  isOpen,
  onClose,
  onSuccess
}: ReceiveStockModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<StockItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadItems();
    }
  }, [isOpen]);

  const loadItems = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const data = await getStockItems(selectedBusiness.id);
      setItems(data);
      
      // Load photos for all items
      await loadProductPhotos(data.map(item => item.id));
    } catch (err) {
      console.error('Error loading items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load items');
    }
  };

  const loadProductPhotos = async (stockItemIds: string[]) => {
    if (stockItemIds.length === 0) return;

    try {
      const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
      
      // Convert to the format expected by the component
      const photoUrls: Record<string, string> = {};
      Object.entries(photoMap).forEach(([itemId, photo]) => {
        photoUrls[itemId] = photo.photo_url;
      });
      
      setProductPhotos(photoUrls);
    } catch (err) {
      console.error('Error loading product photos:', err);
      // Don't show error to user, just continue without photos
    }
  };

  // Component for item icon - shows photo if available, otherwise blue icon
  const ItemIcon = ({ item, size = 'medium' }: { item: StockItem; size?: 'small' | 'medium' }) => {
    const photoUrl = productPhotos[item.id];
    const sizeClasses = size === 'small' ? 'h-10 w-10' : 'h-12 w-12';
    const iconSizeClasses = size === 'small' ? 'h-5 w-5' : 'h-6 w-6';
    
    if (photoUrl) {
      return (
        <div className={`${sizeClasses} rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center`}>
          <img 
            src={photoUrl} 
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, hide it and show the blue icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = `
                  <div class="w-full h-full rounded-lg bg-blue-100 flex items-center justify-center">
                    <svg class="${iconSizeClasses} text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                    </svg>
                  </div>
                `;
              }
            }}
          />
        </div>
      );
    }
    
    // Default blue icon if no photo
    return (
      <div className={`${sizeClasses} rounded-lg bg-blue-100 flex items-center justify-center`}>
        <Package2 className={`${iconSizeClasses} text-blue-600`} />
      </div>
    );
  };

  // Filter items based on search query
  const filteredItems = React.useMemo(() => {
    if (!searchQuery) return [];
    const searchLower = searchQuery.toLowerCase();
    return items.filter(item => {
      // Exclude deleted items
      if (item.is_deleted) return false;
      // Exclude one-time items with 0 quantity
      if (item.is_one_time && item.quantity === 0) return false;
      // Match search query
      return item.name.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.sku?.toLowerCase().includes(searchLower);
    });
  }, [items, searchQuery]);

  // Group items by category
  const itemsByCategory = React.useMemo(() => {
    // Filter out deleted items and one-time items with 0 quantity
    const activeItems = items.filter(item => {
      if (item.is_deleted) return false;
      // Exclude one-time items with 0 quantity
      if (item.is_one_time && item.quantity === 0) return false;
      return true;
    });
    
    const grouped = activeItems.reduce((acc, item) => {
      const categoryId = item.category_id || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = {
          id: categoryId,
          name: item.category?.name || 'Uncategorized',
          items: []
        };
      }
      acc[categoryId].items.push(item);
      return acc;
    }, {} as Record<string, { id: string; name: string; items: StockItem[] }>);

    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  // Filter categories based on search query
  const filteredCategories = itemsByCategory.filter(category => {
    const searchLower = searchQuery.toLowerCase();
    return (
      category.name.toLowerCase().includes(searchLower) ||
      category.items.some(item => 
        item.name.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.sku?.toLowerCase().includes(searchLower)
      )
    );
  });

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setSelectedItem(null);
      setQuantity('');
      setPrice('');
      setNotes('');
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) {
      setError('Please select an item');
      return;
    }

    const receiveQuantity = parseInt(quantity);
    if (isNaN(receiveQuantity) || receiveQuantity <= 0) {
      setError('Please enter a valid quantity');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await createStockTransaction({
          item_id: selectedItem.id,
          type: 'purchase',
          quantity: receiveQuantity,
          price: parseFloat(price) || selectedItem.acquisition_cost,
          notes: notes || null
      });

      onSuccess();
    } catch (err) {
      console.error('Error receiving stock:', err);
      setError(err instanceof Error ? err.message : 'Failed to receive stock');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100010,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[700px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Receive Stock
            </h2>
          </div>

          {/* Content Section */}
          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <div className="flex items-center">
                    <span>{error}</span>
                  </div>
                </div>
              )}

              {/* Item Selection Section */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <label className="block text-sm font-semibold text-gray-900 mb-4">
                  Select Item to Receive
                </label>
                
                {/* Search Input */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                    style={{ fontSize: '16px' }}
                  />
                </div>

                {/* Show search results directly if searching */}
                {searchQuery ? (
                  <div className="space-y-2">
                    {filteredItems.length === 0 ? (
                      <div className="bg-white rounded-[12px] border border-gray-200/60 p-8 shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-center">
                        <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-sm font-semibold text-gray-900 mb-1">No items match your search</p>
                        <p className="text-sm text-gray-500">Try adjusting your search terms</p>
                      </div>
                    ) : (
                      filteredItems.map((item) => (
                        <div key={item.id}>
                          <div
                            className={`bg-white rounded-[12px] border p-4 cursor-pointer active:scale-[0.98] transition-all duration-150 touch-manipulation ${
                              selectedItem?.id === item.id ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-200' : 'border-gray-200/60'
                            } ${
                              item.reorder_point !== null && item.quantity <= item.reorder_point ? 'bg-red-50 border-red-200' : ''
                            } shadow-[0_1px_2px_rgba(0,0,0,0.05)]`}
                            onClick={() => {
                              setSelectedItem(item);
                              setPrice(item.acquisition_cost.toString());
                            }}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <div className="flex items-center">
                              <div className="mr-4">
                                <ItemIcon item={item} size="medium" />
                              </div>
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 flex items-center space-x-2">
                                  <span>{item.name}</span>
                                  <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-1 rounded-lg">
                                    {item.category?.name || 'Uncategorized'}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-600 mt-1 flex items-center space-x-2">
                                  <span>Current: <span className="font-medium text-blue-700">{item.quantity} {item.unit}</span></span>
                                  {item.reorder_point !== null && item.quantity <= item.reorder_point && (
                                    <span className="text-red-600 font-medium bg-red-100 px-2 py-1 rounded-lg">
                                      Below reorder point
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Inline receive form - shows when this item is selected */}
                          {selectedItem?.id === item.id && (
                            <div className="mt-4 p-4 bg-blue-50 rounded-xl space-y-3">
                              <div className="flex items-center gap-3">
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={quantity}
                                  onChange={(e) => setQuantity(e.target.value)}
                                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                  style={{ fontSize: '16px' }}
                                  placeholder="Quantity"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span className="text-sm text-gray-600 font-semibold">{item.unit}(s)</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="relative flex-1">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-gray-500" style={{ fontSize: '16px' }}>$</span>
                                  </div>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                    style={{ fontSize: '16px' }}
                                    placeholder="Cost per unit"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                                <button
                                  type="submit"
                                  disabled={loading || !quantity || !price}
                                  className="px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation whitespace-nowrap"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  {loading ? 'Receiving...' : 'Receive'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredCategories.map((category) => (
                      <div key={category.id} className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                        <button
                          type="button"
                          onClick={() => toggleCategory(category.id)}
                          className={`w-full px-4 py-3 flex items-center justify-between active:bg-gray-100 transition-colors ${
                            category.items.some(item => item.reorder_point !== null && item.quantity <= item.reorder_point) 
                              ? 'bg-red-50' 
                              : 'bg-gray-50'
                          }`}
                          style={{ touchAction: 'manipulation' }}
                        >
                          <div className="flex items-center">
                            <ChevronDown 
                              className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                                expandedCategories.has(category.id) ? 'rotate-180' : ''
                              }`}
                            />
                            <span className={`ml-3 text-sm font-medium ${
                              category.items.some(item => item.reorder_point !== null && item.quantity <= item.reorder_point)
                                ? 'text-red-800'
                                : 'text-green-800'
                            }`}>
                              {category.name}
                            </span>
                            {category.items.some(item => item.reorder_point !== null && item.quantity <= item.reorder_point) && (
                              <span className="ml-2 text-xs text-red-600 font-medium bg-red-100 px-2 py-1 rounded-lg">
                                Low Stock
                              </span>
                            )}
                          </div>
                        </button>

                        {expandedCategories.has(category.id) && (
                          <div className="bg-white divide-y divide-gray-200">
                            {category.items.map((item) => (
                              <div key={item.id}>
                                <div
                                  className={`p-4 cursor-pointer active:bg-gray-100 transition-colors ${
                                    selectedItem?.id === item.id ? 'bg-blue-50' : ''
                                  } ${
                                    item.reorder_point !== null && item.quantity <= item.reorder_point ? 'bg-red-50' : ''
                                  }`}
                                  onClick={() => {
                                    setSelectedItem(item);
                                    setPrice(item.acquisition_cost.toString());
                                  }}
                                  style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                                >
                                  <div className="flex items-center">
                                    <div className="mr-3">
                                      <ItemIcon item={item} size="small" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                      <div className="text-xs text-gray-600 mt-0.5 flex items-center space-x-2">
                                        <span>Current: <span className="font-medium text-blue-700">{item.quantity} {item.unit}</span></span>
                                        {item.reorder_point !== null && item.quantity <= item.reorder_point && (
                                          <span className="text-red-600 font-medium">
                                            (Below reorder point)
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                          {/* Inline receive form - shows when this item is selected */}
                          {selectedItem?.id === item.id && (
                            <div className="px-4 pb-4 bg-blue-50 space-y-3">
                              <div className="flex items-center gap-3">
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={quantity}
                                  onChange={(e) => setQuantity(e.target.value)}
                                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                  style={{ fontSize: '16px' }}
                                  placeholder="Quantity"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span className="text-sm text-gray-600 font-semibold">{item.unit}(s)</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="relative flex-1">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-gray-500" style={{ fontSize: '16px' }}>$</span>
                                  </div>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                    style={{ fontSize: '16px' }}
                                    placeholder="Cost per unit"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                                <button
                                  type="submit"
                                  disabled={loading || !quantity || !price}
                                  className="px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation whitespace-nowrap"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  {loading ? 'Receiving...' : 'Receive'}
                                </button>
                              </div>
                            </div>
                          )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
            
            {/* Note: Submit happens inline now, no bottom button needed */}
            <div className="bg-white border-t border-gray-200 px-6 py-4 hidden">
              <button
                type="submit"
                disabled={true}
                className={`w-full px-6 py-3 text-base font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  loading ? 'bg-gray-600' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                    Receiving...
                  </div>
                ) : (
                  'Receive Stock'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
}